import {
  Box,
  Flex,
  FormLabel,
  Input,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
  formatCommaEmails,
  isValidEmail,
  parseCommaEmails,
} from 'utils/emailHelpers';

// Chip/tag input for one or more email addresses.
// The value in and out is a comma-separated string so it matches how the
// backend stores merchant_email / finance_email and needs no extra mapping.
// Type an email and press comma (or Enter) to turn it into a removable chip.
const EmailChipsInput = ({
  labelText,
  placeholder,
  value,
  onChange,
  onBlur,
  errors,
  name,
  textColor,
  brandStars,
  isValidate = false,
  isDisabled = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [localError, setLocalError] = useState('');

  const emails = parseCommaEmails(value);

  const emitChange = (nextEmails) => {
    onChange?.(formatCommaEmails(nextEmails));
  };

  const addEmail = (raw) => {
    const email = raw.trim();
    if (!email) return true;
    if (!isValidEmail(email)) {
      setLocalError('Please enter a valid email address');
      return false;
    }
    const exists = emails.some((e) => e.toLowerCase() === email.toLowerCase());
    if (!exists) {
      emitChange([...emails, email]);
    }
    setInputValue('');
    setLocalError('');
    return true;
  };

  const removeEmail = (index) => {
    emitChange(emails.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      addEmail(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      removeEmail(emails.length - 1);
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData?.getData('text');
    if (text && /[,\s;]/.test(text)) {
      e.preventDefault();
      const parts = text
        .split(/[,\s;]+/)
        .map((p) => p.trim())
        .filter(Boolean);
      const valid = parts.filter(isValidEmail);
      const invalid = parts.length !== valid.length;
      const merged = [...emails];
      valid.forEach((email) => {
        if (!merged.some((e2) => e2.toLowerCase() === email.toLowerCase())) {
          merged.push(email);
        }
      });
      emitChange(merged);
      setLocalError(invalid ? 'Please enter a valid email address' : '');
    }
  };

  const handleBlur = (e) => {
    // Commit whatever is typed when focus leaves the field
    addEmail(inputValue);
    onBlur?.(e);
  };

  const errorMessage = errors?.message || localError;

  return (
    <Box>
      <FormLabel
        display="flex"
        ms="4px"
        fontSize="sm"
        fontWeight="500"
        color={textColor}
        mb="8px"
      >
        {labelText}
        {isValidate && <Text color={brandStars}>*</Text>}
      </FormLabel>
      <Flex
        wrap="wrap"
        align="center"
        gap="8px"
        minH="48px"
        px="12px"
        py="8px"
        bg={isDisabled ? '#f5f5f5' : 'white'}
        border="1px solid #ccc"
        borderRadius="16px"
        cursor={isDisabled ? 'not-allowed' : 'text'}
      >
        {emails.map((email, index) => (
          <Tag
            key={`${email}-${index}`}
            size="md"
            borderRadius="full"
            variant="subtle"
            colorScheme="blue"
          >
            <TagLabel>{email}</TagLabel>
            {!isDisabled && (
              <TagCloseButton
                aria-label={`Remove ${email}`}
                onClick={() => removeEmail(index)}
              />
            )}
          </Tag>
        ))}
        <Input
          flex="1"
          minW="140px"
          variant="unstyled"
          fontSize="sm"
          fontWeight="500"
          name={name}
          placeholder={emails.length === 0 ? placeholder : ''}
          value={inputValue}
          isDisabled={isDisabled}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (localError) setLocalError('');
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
        />
      </Flex>
      {errorMessage && (
        <p style={{ color: 'red', fontSize: '14px' }}>{errorMessage}</p>
      )}
    </Box>
  );
};

EmailChipsInput.propTypes = {
  labelText: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  errors: PropTypes.object,
  name: PropTypes.string.isRequired,
  textColor: PropTypes.string,
  brandStars: PropTypes.string,
  isValidate: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

export default EmailChipsInput;
